// Bir kişinin belgeleri: personel sayfasının Belgeler sekmesi ile "Belgelerim"
// aynı tabloyu çizer, yalnız kimin belgeleri olduğu değişir.
//
// İndirme sütunu YOK. Belge kaydı yalnız künyedir; nesne deposu ve indirme ucu
// henüz yok (routes/documents.ts "NESNE DEPOSU HENÜZ YOK"). Çizimdeki indirme
// simgesi, toplu indirme ve yükleme bu yüzden çizilmedi. Geçerlilik/süre dolumu
// da çizilmedi: belge satırında böyle bir alan yok.

import { useTranslation } from "react-i18next";

import { Badge, Card, DataTable, Select, type BadgeTone, type DataTableColumn } from "@novemai/components";

import { useKeysetQuery } from "@/api/keyset";
import { useTimeZone } from "@/api/use-time-zone";
import { TabQueryState } from "@/features/employees/detail-tab-state";
import { formatInstant } from "@/features/reports/labels";
import { useFilterParam } from "@/lib/use-url-filters";

import { DOCUMENT_CATEGORIES, fetchEmployeeDocumentsPage, type DocumentCategory, type DocumentRow } from "./api";

const LIFECYCLE_TONE: Readonly<Record<string, BadgeTone>> = {
  draft: "neutral",
  published: "info",
  signed: "success",
  archived: "neutral",
  legal_hold: "warning",
  purged: "neutral",
};

export function EmployeeDocumentsTable({
  employeeId,
}: {
  readonly employeeId: string;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  // Server-side: the list pages by offset, so narrowing a loaded page would hide
  // matches on pages not yet read.
  const [categoryParam, setCategory] = useFilterParam("category");
  const category = (DOCUMENT_CATEGORIES as readonly string[]).includes(categoryParam)
    ? (categoryParam as DocumentCategory)
    : undefined;
  const query = useKeysetQuery<DocumentRow>({
    queryKey: ["documents", "employee", employeeId, category ?? "all"],
    fetchPage: (cursor) => fetchEmployeeDocumentsPage(employeeId, cursor, category),
  });

  const columns: readonly DataTableColumn<DocumentRow>[] = [
    {
      id: "title",
      header: t("people.documents.column.title"),
      cell: (row): React.ReactNode => (
        <span className="text-[13.5px] font-medium text-[var(--fg-primary)]">{row.title}</span>
      ),
    },
    {
      id: "category",
      header: t("people.documents.column.category"),
      cell: (row): React.ReactNode => (
        <Badge tone="neutral">{t(`people.documents.category.${row.category}`)}</Badge>
      ),
    },
    {
      id: "version",
      header: t("people.documents.column.version"),
      cell: (row): React.ReactNode => (
        <span className="font-mono text-[12px] text-[var(--fg-muted)]">
          {t("people.documents.version_value", { version: row.version })}
        </span>
      ),
    },
    {
      id: "created",
      header: t("people.documents.column.created"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12.5px] text-[var(--fg-muted)]">
          {formatInstant(i18n.language, timeZone, row.created_at)}
        </span>
      ),
    },
    {
      id: "state",
      header: t("people.documents.column.state"),
      cell: (row): React.ReactNode => (
        <Badge tone={LIFECYCLE_TONE[row.lifecycle_state] ?? "neutral"}>
          {t(`people.documents.lifecycle.${row.lifecycle_state}`)}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <Select
        aria-label={t("people.documents.filter_category")}
        value={category ?? ""}
        onChange={(event): void => setCategory(event.target.value)}
        className="w-full sm:w-[240px]"
      >
        <option value="">{t("people.documents.filter_all")}</option>
        {DOCUMENT_CATEGORIES.map((value) => (
          <option key={value} value={value}>
            {t(`people.documents.category.${value}`)}
          </option>
        ))}
      </Select>
      <TabQueryState
        query={query}
        isEmpty={query.items.length === 0}
        emptyTitle={t(category === undefined ? "people.documents.empty_title" : "people.documents.empty_filtered_title")}
        emptyDescription={t(category === undefined ? "people.documents.empty_description" : "people.documents.empty_filtered_description")}
      >
        <Card className="overflow-hidden">
          <DataTable<DocumentRow>
            caption={t("people.documents.caption")}
            columns={columns}
            rows={query.items}
            rowKey={(row): string => row.document_id}
            hasNextPage={query.hasNextPage}
            onLoadMore={query.fetchNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            loadMoreLabel={t("people.detail.tabs.load_more")}
          />
        </Card>
      </TabQueryState>
    </div>
  );
}
